import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { ShipmentOrderSpec } from '@core/models/job-file/shipment-order-spec.model';

export interface OrderFilters {
  deliveryOnOrBeforeDate: Date | null;
  deliveryOnOrAfterDate: Date | null;
  weightMin: number;
  weightMax: number;
  capacityMin: number;
  capacityMax: number;
  weightSliderMin?: number;
  weightSliderMax?: number;
  capacitySliderMin?: number;
  capacitySliderMax?: number;
}

@Component({
  selector: 'hmt-order-filters',
  templateUrl: './order-filters.component.html',
  styleUrls: ['./order-filters.component.scss'],
  standalone: false
})
export class OrderFiltersComponent implements OnInit {
  // Filter form controls
  deliveryOnOrBeforeDateControl = new FormControl<Date | null>(null);
  deliveryOnOrAfterDateControl = new FormControl<Date | null>(null);
  
  // Weight range slider values
  weightMin = 20;
  weightMax = 70;
  weightSliderMin = 20;
  weightSliderMax = 70;
  
  // Capacity range slider values
  capacityMin = 0;
  capacityMax = 100;
  capacitySliderMin = 0;
  capacitySliderMax = 100;

  constructor(
    public dialogRef: MatDialogRef<OrderFiltersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { orders: ShipmentOrderSpec[]; currentFilters?: OrderFilters }
  ) {}

  ngOnInit(): void {
    // Extract unique dropoff locations and consignees from orders
    this.extractFilterOptions();
    
    // Apply existing filters if provided
    if (this.data.currentFilters) {
      this.applyExistingFilters(this.data.currentFilters);
    }
  }

  private extractFilterOptions(): void {
    let maxWeight = 0;
    let maxCapacity = 0;

    this.data.orders.forEach(order => {
      if (order.totalWeight && order.totalWeight > maxWeight) {
        maxWeight = Math.ceil(order.totalWeight);
      }
      if (order.totalCBM && order.totalCBM > maxCapacity) {
        maxCapacity = Math.ceil(order.totalCBM);
      }
    });
    
    // Set slider max values based on actual data
    if (maxWeight > 0) {
      this.weightSliderMax = Math.max(70, maxWeight);
      this.weightMax = this.weightSliderMax;
    }
    if (maxCapacity > 0) {
      this.capacitySliderMax = Math.max(100, maxCapacity);
      this.capacityMax = this.capacitySliderMax;
    }
  }

  private applyExistingFilters(filters: OrderFilters): void {
    this.deliveryOnOrBeforeDateControl.setValue(filters.deliveryOnOrBeforeDate);
    this.deliveryOnOrAfterDateControl.setValue(filters.deliveryOnOrAfterDate);
    this.weightMin = filters.weightMin || this.weightSliderMin;
    this.weightMax = filters.weightMax || this.weightSliderMax;
    this.capacityMin = filters.capacityMin || this.capacitySliderMin;
    this.capacityMax = filters.capacityMax || this.capacitySliderMax;
  }

  resetDeliveryDates(): void {
    this.deliveryOnOrBeforeDateControl.setValue(null);
    this.deliveryOnOrAfterDateControl.setValue(null);
  }

  resetAll(): void {
    // Reset all filters to default values
    this.deliveryOnOrBeforeDateControl.setValue(null);
    this.deliveryOnOrAfterDateControl.setValue(null);
    this.weightMin = this.weightSliderMin;
    this.weightMax = this.weightSliderMax;
    this.capacityMin = this.capacitySliderMin;
    this.capacityMax = this.capacitySliderMax;
    
    // Close dialog without applying filters (return null to indicate no filters)
    this.dialogRef.close(null);
  }

  resetWeight(): void {
    this.weightMin = this.weightSliderMin;
    this.weightMax = this.weightSliderMax;
  }

  resetCapacity(): void {
    this.capacityMin = this.capacitySliderMin;
    this.capacityMax = this.capacitySliderMax;
  }

  applyFilters(): void {
    const filters: OrderFilters = {
      deliveryOnOrBeforeDate: this.deliveryOnOrBeforeDateControl.value,
      deliveryOnOrAfterDate: this.deliveryOnOrAfterDateControl.value,
      weightMin: this.weightMin,
      weightMax: this.weightMax,
      capacityMin: this.capacityMin,
      capacityMax: this.capacityMax,
      weightSliderMin: this.weightSliderMin,
      weightSliderMax: this.weightSliderMax,
      capacitySliderMin: this.capacitySliderMin,
      capacitySliderMax: this.capacitySliderMax
    };

    this.dialogRef.close(filters);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  formatDate(date: Date | null): string {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatWeight(value: number): string {
    return `${value} Kg`;
  }

  formatCapacity(value: number): string {
    return `${value} Cbm`;
  }

  onWeightMinChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.weightMin = Math.min(value, this.weightMax - 1);
  }

  onWeightMaxChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.weightMax = Math.max(value, this.weightMin + 1);
  }

  onCapacityMinChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.capacityMin = Math.min(value, this.capacityMax - 1);
  }

  onCapacityMaxChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.capacityMax = Math.max(value, this.capacityMin + 1);
  }

  getWeightSliderBackground(): string {
    const range = this.weightSliderMax - this.weightSliderMin;
    const minPercent = ((this.weightMin - this.weightSliderMin) / range) * 100;
    const maxPercent = ((this.weightMax - this.weightSliderMin) / range) * 100;
    return `linear-gradient(to right, rgba(142, 134, 222, 0.2) 0%, rgba(142, 134, 222, 0.2) ${minPercent}%, #6F68B9 ${minPercent}%, #6F68B9 ${maxPercent}%, rgba(142, 134, 222, 0.2) ${maxPercent}%, rgba(142, 134, 222, 0.2) 100%)`;
  }

  getCapacitySliderBackground(): string {
    const range = this.capacitySliderMax - this.capacitySliderMin;
    const minPercent = ((this.capacityMin - this.capacitySliderMin) / range) * 100;
    const maxPercent = ((this.capacityMax - this.capacitySliderMin) / range) * 100;
    return `linear-gradient(to right, rgba(142, 134, 222, 0.2) 0%, rgba(142, 134, 222, 0.2) ${minPercent}%, #6F68B9 ${minPercent}%, #6F68B9 ${maxPercent}%, rgba(142, 134, 222, 0.2) ${maxPercent}%, rgba(142, 134, 222, 0.2) 100%)`;
  }
}

